import React, { useEffect, useMemo, useRef, useState } from "react";
import DepositCurrencyInput from "./global/inputs/DepositCurrencyInput";
import InstrumentInput from "./global/inputs/InstrumentInput";
import StopLossInput from "./global/inputs/StopLossInput";
import BalanceInput from "./global/inputs/BalanceInput";
import RiskInput from "./global/inputs/RiskInput";
import PriceInput from "./global/inputs/PriceInput";
import Calculator from "./global/Calculator";
import UnitsOf1Lot from "./global/inputs/UnitsOf1Lot";
import OnePip from "./global/inputs/OnePip";
import { useLanguage } from "../lib/LanguageContext";
import {
  useArrangeMode,
  useElementSize,
} from "../lib/custom-hooks";
import { clearAllCaches, formatResultNum, getCache, getMobileAppSettings, setCaches, getCaches } from "../lib/funcs";
import { DEFAULTS, OPERATIONS, STOPLOSS_TYPES } from "../lib/constants";
import { useSelector } from "react-redux";
import { setSavedFormInfo } from "../redux/variables/varActions";
import { useDispatch } from "react-redux";

function LotSizeCalculator(props: { noLine?: boolean }) {
  const { languageData } = useLanguage();
  const arrangeMode = useArrangeMode();
  const elementSize = useElementSize();
  const defaultResult = useMemo(() => [
    { name: languageData.position_size, result: "----" },
    { name: languageData.money_at_risk, result: "----" },
  ], [languageData.money_at_risk, languageData.position_size]);
  const [tradeSizeInfo, setTradeSizeInfo] = useState<ITradeSizeInfo | undefined>(getCache("lotsize", "tradeSizeInfo"));

  const [switches, setSwitches] = useState(getCache("lotsize", "switches"));
  const [instruments, setInstruments] = useState<IInstrument[]>(getCache("lotsize", "instruments"));
  const [depositCurrencies, setDepositCurrencies] = useState<IDepositCurrency[]>(getCache("lotsize", "depositCurrencies"));
  const [depositableCurrencies, setDepositableCurrencies] = useState<IDepositCurrency[]>(getCache("lotsize", "depositableCurrencies"));
  const [selectedInstrument, setSelectedInstrument] = useState<IInstrument | undefined>(getCache("lotsize", "selectedInstrument"));
  const [priceInstrument, setPriceInstrument] = useState<(IInstrument & { operation: string }) | undefined>(getCache("lotsize", "priceInstrument"));
  const [selectedDepositCurrency, setSelectedDepositCurrency] = useState<IDepositCurrency | undefined>(getCache("lotsize", "selectedDepositCurrency"));
  const [price, setPrice] = useState<string | number>(getCache("lotsize", "price"));
  const [openPrice, setOpenPrice] = useState<string | number>(getCache("lotsize", "openPrice"));
  const [stopLoss, setStopLoss] = useState<string>(getCache("lotsize", "stopLoss"));
  const [balance, setBalance] = useState<string>(getCache("lotsize", "balance"));
  const [risk, setRisk] = useState<string | number>(getCache("lotsize", "risk"));
  const [riskType, setRiskType] = useState<"CURRENCY" | "PERCENT">(getCache("lotsize", "riskType"));
  const [result, setResult] = useState<IResult>(defaultResult);
  const [lotUnits, setLotUnits] = useState<number | string>(getCache("lotsize", "lotUnits"));
  const [onePip, setOnePip] = useState<number | string>(getCache("lotsize", "onePip"));
  const [stopLossType, setStopLossType] = useState<number>(getCache("lotsize", "stopLossType"));

  const settings = useSelector((state: { variables: { settings: ISettings } }) => state.variables.settings);
  const isFirstRender = useRef(true);
  const dispatch = useDispatch();

  useEffect(() => {
    if(isFirstRender.current) {
      setTimeout(() => {
        isFirstRender.current = false;
      }, 500);
      return;
    }
    const settings = getMobileAppSettings();
    if(!settings.rememberChanges) {
      clearAllCaches();
      setTradeSizeInfo(DEFAULTS.lotsize.tradeSizeInfo)
      setSwitches(DEFAULTS.lotsize.switches);
      setInstruments(instruments => {
        setSelectedInstrument(instruments.find(i => i.QuoteCurrency === "USD" && i.BaseCurrency === "EUR"));
        return instruments;
      });
      setSelectedDepositCurrency(DEFAULTS.lotsize.selectedDepositCurrency);
      setBalance(DEFAULTS.lotsize.balance);
      setRisk(DEFAULTS.lotsize.risk);
      setRiskType(DEFAULTS.lotsize.riskType);
      setOnePip(DEFAULTS.lotsize.onePip);
      setLotUnits(DEFAULTS.lotsize.lotUnits);
      setStopLoss(DEFAULTS.lotsize.stopLoss);
      dispatch(setSavedFormInfo({}));
      setStopLossType(DEFAULTS.lotsize.stopLossType);
      console.log("load default data...");
    }
  }, [settings.rememberChanges]);

  useEffect(() => {
    setCaches("lotsize", {
      ...getCaches("lotsize"),
      switches,
      instruments,
      depositCurrencies,
      depositableCurrencies,
      selectedInstrument,
      priceInstrument,
      selectedDepositCurrency,
      price,
      openPrice,
      stopLoss,
      balance,
      risk,
      riskType,
      lotUnits,
      onePip,
      stopLossType,
      tradeSizeInfo,
    });
  }, [
    switches,
    instruments,
    depositCurrencies,
    depositableCurrencies,
    selectedInstrument,
    priceInstrument,
    selectedDepositCurrency,
    price,
    openPrice,
    stopLoss,
    balance,
    risk,
    riskType,
    lotUnits,
    onePip,
    stopLossType,
    tradeSizeInfo,
  ]);

  useEffect(() => {
    if (!selectedInstrument || !depositCurrencies.length)
      return setDepositableCurrencies([]);
    const filteredInstruments = instruments.filter(
      (ins) =>
        selectedInstrument.QuoteCurrency === ins.BaseCurrency ||
        selectedInstrument.QuoteCurrency === ins.QuoteCurrency
    );
    setDepositableCurrencies(
      depositCurrencies.filter(
        (dc) =>
          filteredInstruments.filter(
            (fi) =>
              fi.BaseCurrency === dc.GlobalCurrencyName ||
              fi.QuoteCurrency === dc.GlobalCurrencyName
          ).length
      )
    );
  }, [depositCurrencies, selectedInstrument, instruments]);

  useEffect(() => {
    if (
      !selectedInstrument ||
      !selectedDepositCurrency ||
      selectedInstrument.QuoteCurrency ===
        selectedDepositCurrency.GlobalCurrencyName
    ) {
      setPriceInstrument(undefined);
      return;
    }
    if (
      selectedInstrument.BaseCurrency ===
      selectedDepositCurrency.GlobalCurrencyName
    ) {
      setPriceInstrument({ ...selectedInstrument, operation: OPERATIONS.MUL });
      setPrice(Number(selectedInstrument.SymbolRate.toFixed(6)));
      return;
    }
    let pInstrument = selectedInstrument;
    let operation = OPERATIONS.MUL;
    setInstruments(instruments => {
      instruments.forEach((i) => {
        if (
          i.QuoteCurrency === selectedInstrument.QuoteCurrency &&
          i.BaseCurrency === selectedDepositCurrency.GlobalCurrencyName
        ) {
          pInstrument = i;
          operation = OPERATIONS.MUL;
        }
        if (
          i.QuoteCurrency === selectedDepositCurrency.GlobalCurrencyName &&
          i.BaseCurrency === selectedInstrument.QuoteCurrency
        ) {
          pInstrument = i;
          operation = OPERATIONS.DIV;
        }
      });
      if (pInstrument) {
        setPriceInstrument({ ...pInstrument, operation });
        setPrice(Number(pInstrument.SymbolRate.toFixed(6)));
      }
      return instruments;
    });
  }, [selectedInstrument, selectedDepositCurrency, languageData]);

  const calculate: React.MouseEventHandler<HTMLInputElement> = (e) => {
    e.preventDefault();
    let result: string | number = "";
    if (!selectedInstrument)
      return setResult([{ name: "Error", result: "No Instrument Selected" }]);
    if (!selectedDepositCurrency)
      return setResult([
        { name: "Error", result: "No Deposit Currency Selected" },
      ]);
    if (!balance)
      return setResult([
        { name: "Error", result: "Please Enter Your Account Balance" },
      ]);
    if (!stopLoss)
      return setResult([
        { name: "Error", result: "Entering StopLoss Is Required" },
      ]);
    if (!risk)
      return setResult([
        { name: "Error", result: "Entering Risk Amount Is Required" },
      ]);
    if (!lotUnits || !Number(lotUnits))
      return setResult([
        {
          name: "Error",
          result: "Units of 1 lot can't be " + (lotUnits || "empty"),
        },
      ]);
    let tickSize = Number(onePip);

    let sl =
      STOPLOSS_TYPES.PIPS === stopLossType
        ? Number(stopLoss)
        : Math.abs(Number(openPrice) - Number(stopLoss)) / Number(onePip);

    const moneyAtRisk =
      riskType === "CURRENCY"
        ? Number(risk)
        : Number(balance) * (Number(risk) / 100);
    let r1 = moneyAtRisk / sl;
    if (
      selectedInstrument?.BaseCurrency ===
      selectedDepositCurrency?.GlobalCurrencyName
    ) {
      r1 = r1 * Number(price);
    } else if (
      selectedInstrument?.QuoteCurrency ===
      selectedDepositCurrency?.GlobalCurrencyName
    ) {
    } else {
      if (priceInstrument?.operation === OPERATIONS.MUL) {
        r1 = r1 * Number(price);
      } else {
        r1 = r1 / Number(price);
      }
    }
    const market = selectedInstrument.Market;
    const fractionCount = 
      market === "Forex" ? 0 : (price + "").split(".")[0].length + 2;
    result = Number((r1 / tickSize / Number(lotUnits)).toFixed(6));
    const unitSize =
      market === "Forex"
        ? Math.round(result * Number(lotUnits))
        : result * Number(lotUnits);
    const percentAtRisk = Number(
      (moneyAtRisk / (Number(balance) / 100)).toFixed(3)
    );

    setResult([
      {
        name: languageData.position_size,
        result: [
          Number(result.toFixed(3)) + " " + languageData.lots,
          formatResultNum(unitSize, fractionCount) + " " + languageData.units,
        ],
      },
      {
        name: languageData.money_at_risk,
        result: [
          formatResultNum(moneyAtRisk, fractionCount || 2) +
            " " +
            selectedDepositCurrency.GlobalCurrencyName,
          percentAtRisk + "%",
        ],
      },
    ]);
  };

  useEffect(() => {
    setResult(defaultResult);
  }, [switches, selectedInstrument, selectedDepositCurrency, tradeSizeInfo?.value, price, stopLoss, balance, lotUnits, risk, riskType, defaultResult]);

  return (
    <Calculator
      type="lotsize"
      noLine={props.noLine}
      result={result}
      calculate={calculate}
      setInstruments={setInstruments}
      setSelectedInstrument={setSelectedInstrument}
      setDepositCurrencies={setDepositCurrencies}
      setSelectedDepositCurrency={setSelectedDepositCurrency}
      depositableCurrencies={depositableCurrencies}
      selectedInstrument={selectedInstrument}
      selectedDepositCurrency={selectedDepositCurrency}
    >
      <InstrumentInput
        className={`col-${Math.min(12, elementSize * 2)}`}
        instruments={instruments}
        switches={switches}
        setSwitches={setSwitches}
        selectedInstrument={selectedInstrument}
        setSelectedInstrument={setSelectedInstrument}
        setInstruments={setInstruments}
      />

      <DepositCurrencyInput
        className={`col-${elementSize}`}
        depositCurrencies={depositableCurrencies}
        setSelectedDepositCurrency={setSelectedDepositCurrency}
        selectedDepositCurrency={selectedDepositCurrency}
      />

      {arrangeMode !== 2 && arrangeMode !== 3 ? null : (
        <BalanceInput
          className={`col-${elementSize}`}
          balance={balance}
          setBalance={setBalance}
        />
      )}

      <RiskInput
        className={`col-${Math.min(12, elementSize * 2)}`}
        riskType={riskType}
        selectedDepositCurrency={selectedDepositCurrency}
        setRiskType={setRiskType}
        risk={risk}
        balance={balance}
        setRisk={setRisk}
      />

      {arrangeMode !== 2 && arrangeMode !== 3 ? (
        <BalanceInput
          className={`col-${elementSize}`}
          balance={balance}
          setBalance={setBalance}
        />
      ) : null}

      <StopLossInput
        selectedInstrument={selectedInstrument}
        price={selectedInstrument?.SymbolRate || "1"}
        openPrice={openPrice}
        setOpenPrice={setOpenPrice}
        onePip={onePip}
        STOPLOSS_TYPES={STOPLOSS_TYPES}
        stopLossType={stopLossType}
        setStopLossType={setStopLossType}
        stopLoss={stopLoss}
        setStopLoss={setStopLoss}
      />

      <PriceInput
        setSelectedDepositCurrency={setSelectedDepositCurrency}
        setDepositCurrencies={setDepositCurrencies}
        setInstruments={setInstruments}
        setSelectedInstrument={setSelectedInstrument}
        className={`col-${Math.min(12, elementSize * 2)}`}
        priceInstrument={priceInstrument}
        price={price}
        setPrice={setPrice}
      />

      <UnitsOf1Lot
        className={`col-${elementSize}`}
        selectedInstrument={selectedInstrument}
        setLotUnits={setLotUnits}
        setTradeSizeInfo={setTradeSizeInfo}
        lotUnits={lotUnits}
        calc="lotsize"
      />

      <OnePip
        className={`col-${elementSize}`}
        selectedInstrument={selectedInstrument}
        setOnePip={setOnePip}
        onePip={onePip}
        calc={"lotsize"}
      />
    </Calculator>
  );
}

export default LotSizeCalculator;
